// A CONSIGNAÇÃO FACULTATIVA OPERADA UMA A UMA (#281).
//
// O arquivo mensal é o retrato do mês inteiro; aqui a averbadora inclui, altera
// o valor ou encerra UMA consignação, em nome da consignatária, com motivo. As
// regras são as do arquivo — a API recusa com o motivo, e a tela o mostra. O
// arquivo seguinte da mesma competência ainda prevalece: ele é o retrato.

import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  Modal,
  Pagination,
  Pill,
  SelectField,
  TextField,
  TextareaField,
  fmtBRL,
  type Column,
} from "@atlas/ui/web";
import type { ConsignacaoDoCredor, HistoricoDaConsignacao, ResultadoOperacaoConsignacao } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { useFiltros } from "../../lib/use-filtros";

const POR_PAGINA = 25;

/** "120,50" → 12050. Vazio ou inválido → null. */
export function centavosDe(texto: string): number | null {
  const limpo = texto.trim().replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(limpo)) return null;
  const c = Math.round(Number(limpo) * 100);
  return c > 0 ? c : null;
}

const competenciaOk = (c: string) => /^\d{4}(0[1-9]|1[0-2])$/.test(c);
const motivoOk = (m: string) => m.trim().length >= 5;

/** O aviso de quem operou depois da emissão do movimento. */
function avisoDoResultado(r: ResultadoOperacaoConsignacao): string {
  return r.movimentoDesatualizado
    ? "Gravado. O movimento desta competência já foi emitido: a operação só vale numa retificação."
    : "Gravado.";
}

const ROTULO_OPERACAO: Record<HistoricoDaConsignacao["operacoes"][number]["tipo"], string> = {
  inclusao: "Inclusão",
  alteracao_valor: "Alteração de valor",
  encerramento: "Encerramento",
};

export function ConsignacoesDoCredor({
  opcoesCredor,
  opcoesOrgao,
}: {
  opcoesCredor: { value: string; label: string }[];
  opcoesOrgao: { value: string; label: string }[];
}) {
  const [credorId, setCredorId] = useState<number | null>(null);
  const [unidadeId, setUnidadeId] = useState<number | null>(null);
  const [matricula, setMatricula] = useState("");
  const [rubrica, setRubrica] = useState("");
  const [pagina, setPagina] = useState(1);
  const [incluindo, setIncluindo] = useState(false);
  const [alterando, setAlterando] = useState<ConsignacaoDoCredor | null>(null);
  const [encerrando, setEncerrando] = useState<ConsignacaoDoCredor | null>(null);
  const [vendo, setVendo] = useState<ConsignacaoDoCredor | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const { filtrar } = useFiltros(
    [
      // Credor e orgao sao pre-condicao: sem eles nao ha lista.
      { contexto: true, ligado: credorId !== null, limpar: () => setCredorId(null) },
      { contexto: true, ligado: unidadeId !== null, limpar: () => setUnidadeId(null) },
      { nome: "matricula", ligado: matricula !== "", limpar: () => setMatricula("") },
      { nome: "rubrica", ligado: rubrica !== "", limpar: () => setRubrica("") },
    ],
    setPagina,
  );
  const pronto = credorId !== null && unidadeId !== null;
  const q = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "consignacoes", unidadeId, matricula, rubrica, pagina],
    queryFn: () =>
      atlas.admin.consignatarias.listarConsignacoes(credorId!, {
        unidadeId: unidadeId!,
        matricula: matricula || undefined,
        rubrica: rubrica || undefined,
        limit: POR_PAGINA,
        offset: (pagina - 1) * POR_PAGINA,
      }),
    enabled: pronto,
    placeholderData: keepPreviousData,
  });

  const colunas: Column<ConsignacaoDoCredor>[] = [
    { key: "matricula", header: "Matrícula", mono: true, render: (c) => `${c.matricula} · FF ${c.folha}` },
    { key: "rubrica", header: "Rubrica", mono: true },
    { key: "comportamento", header: "Comportamento" },
    {
      key: "valor",
      header: `Valor em ${q.data?.competencia ?? "—"}`,
      render: (c) =>
        c.valorVigenteCentavos == null ? "—" : c.valorVigenteCentavos === 0 ? <Pill variant="expirado">Encerrada</Pill> : fmtBRL(c.valorVigenteCentavos / 100),
    },
    {
      key: "pedido",
      header: "Pedido do servidor",
      render: (c) => (c.solicitacaoAbertaId ? <Pill variant="pendente">Cancelamento pedido</Pill> : "—"),
    },
  ];

  return (
    <>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <SelectField
            label="Consignatária"
            value={credorId === null ? "" : String(credorId)}
            onChange={(e) => filtrar(setCredorId)(e.target.value ? Number(e.target.value) : null)}
            options={[{ value: "", label: "Escolha a consignatária" }, ...opcoesCredor]}
          />
          <SelectField
            label="Órgão/entidade"
            value={unidadeId === null ? "" : String(unidadeId)}
            onChange={(e) => filtrar(setUnidadeId)(e.target.value ? Number(e.target.value) : null)}
            options={[{ value: "", label: "Escolha o órgão/entidade" }, ...opcoesOrgao]}
          />
          <TextField label="Matrícula" value={matricula} onChange={(e) => filtrar(setMatricula)(e.target.value.trim())} />
          <TextField label="Rubrica" value={rubrica} onChange={(e) => filtrar(setRubrica)(e.target.value.trim())} />
        </div>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => setIncluindo(true)} disabled={!pronto}>
            Incluir consignação
          </Button>
        </div>
      </Card>

      {aviso && <Card><p role="status" style={{ margin: 0 }}>{aviso}</p></Card>}

      <Card>
        {!pronto ? (
          <EstadoVazio titulo="Escolha a consignatária e o órgão/entidade." />
        ) : (
          <>
            {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar as consignações.")}</p>}
            <DataTable
              caption="Consignações da consignatária"
              columns={colunas}
              rows={q.data?.itens ?? []}
              rowKey={(c) => c.id}
              loading={q.isLoading}
              emptyState={<EstadoVazio titulo="Nenhuma consignação deste credor no órgão." />}
              actions={(c) => (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Button size="sm" variant="ghost" onClick={() => setVendo(c)}>Histórico</Button>
                  {(c.valorVigenteCentavos ?? 0) > 0 ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setAlterando(c)}>Alterar valor</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEncerrando(c)}>Encerrar</Button>
                    </>
                  ) : null}
                </div>
              )}
            />
            <Pagination
              total={q.data?.total ?? 0}
              pagina={pagina}
              porPagina={POR_PAGINA}
              onPagina={setPagina}
              onPorPagina={() => undefined}
              rotulo="consignações"
            />
          </>
        )}
      </Card>

      {incluindo && pronto ? (
        <IncluirConsignacaoModal credorId={credorId} unidadeId={unidadeId} onFechar={() => setIncluindo(false)} onFeito={setAviso} />
      ) : null}
      {alterando && credorId !== null ? (
        <AlterarValorModal credorId={credorId} consignacao={alterando} onFechar={() => setAlterando(null)} onFeito={setAviso} />
      ) : null}
      {encerrando && credorId !== null ? (
        <EncerrarConsignacaoModal
          credorId={credorId}
          consignacaoId={encerrando.id}
          descricao={`Matrícula ${encerrando.matricula}, rubrica ${encerrando.rubrica}`}
          solicitacaoId={encerrando.solicitacaoAbertaId ?? undefined}
          onFechar={() => setEncerrando(null)}
          onFeito={setAviso}
        />
      ) : null}
      {vendo && credorId !== null ? (
        <HistoricoModal credorId={credorId} consignacao={vendo} onFechar={() => setVendo(null)} />
      ) : null}
    </>
  );
}

function useInvalidar() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["admin", "consignatarias"] });
    void qc.invalidateQueries({ queryKey: ["admin", "consignacoes", "solicitacoes"] });
  };
}

function IncluirConsignacaoModal({
  credorId,
  unidadeId,
  onFechar,
  onFeito,
}: {
  credorId: number;
  unidadeId: number;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const invalidar = useInvalidar();
  const [ff, setFf] = useState("");
  const [matricula, setMatricula] = useState("");
  const [rubrica, setRubrica] = useState("");
  const [valor, setValor] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [motivo, setMotivo] = useState("");
  const centavos = centavosDe(valor);
  const valido =
    ff.trim() !== "" && matricula.trim() !== "" && rubrica.trim() !== "" && centavos !== null &&
    competenciaOk(inicio) && (fim === "" || competenciaOk(fim)) && motivoOk(motivo);
  const salvar = useMutation({
    mutationFn: () =>
      atlas.admin.consignatarias.incluirConsignacao(credorId, {
        unidadeId, ff: ff.trim(), matricula: matricula.trim(), rubrica: rubrica.trim(), valorCentavos: centavos!,
        competenciaInicio: inicio, ...(fim ? { competenciaFim: fim } : {}), motivo: motivo.trim(),
      }),
    onSuccess: (r) => { invalidar(); onFeito(avisoDoResultado(r)); onFechar(); },
  });
  return (
    <Modal
      open
      title="Incluir consignação"
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Incluindo…" : "Incluir"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          <TextField label="FF" value={ff} onChange={(e) => setFf(e.target.value)} />
          <TextField label="Matrícula" value={matricula} onChange={(e) => setMatricula(e.target.value)} />
          <TextField label="Rubrica" value={rubrica} onChange={(e) => setRubrica(e.target.value)} />
          <TextField label="Valor mensal (R$)" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="120,00" />
          <TextField label="Competência inicial" value={inicio} placeholder="AAAAMM" onChange={(e) => setInicio(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          <TextField
            label="Competência final"
            value={fim}
            placeholder="AAAAMM"
            hint="Só para prazo. Vazio = o comportamento declarado da rubrica."
            onChange={(e) => setFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        </div>
        <TextareaField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} hint="Fica no histórico da consignação e na auditoria." />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível incluir.")}</p>}
      </div>
    </Modal>
  );
}

function AlterarValorModal({
  credorId,
  consignacao,
  onFechar,
  onFeito,
}: {
  credorId: number;
  consignacao: ConsignacaoDoCredor;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const invalidar = useInvalidar();
  const [valor, setValor] = useState("");
  const [inicio, setInicio] = useState("");
  const [motivo, setMotivo] = useState("");
  const centavos = centavosDe(valor);
  const valido = centavos !== null && competenciaOk(inicio) && motivoOk(motivo);
  const salvar = useMutation({
    mutationFn: () =>
      atlas.admin.consignatarias.alterarValorConsignacao(credorId, consignacao.id, {
        competenciaInicio: inicio, valorCentavos: centavos!, motivo: motivo.trim(),
      }),
    onSuccess: (r) => { invalidar(); onFeito(avisoDoResultado(r)); onFechar(); },
  });
  return (
    <Modal
      open
      title="Alterar valor"
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Gravando…" : "Alterar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ margin: 0 }}>
          Matrícula {consignacao.matricula}, rubrica {consignacao.rubrica}. O valor novo vale a partir da competência; o anterior
          termina na competência de antes.
        </p>
        <TextField label="Novo valor mensal (R$)" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="150,00" />
        <TextField label="A partir da competência" value={inicio} placeholder="AAAAMM" onChange={(e) => setInicio(e.target.value.replace(/\D/g, "").slice(0, 6))} />
        <TextareaField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível alterar.")}</p>}
      </div>
    </Modal>
  );
}

/**
 * Encerrar: a competência informada é a ÚLTIMA com desconto. Com `solicitacaoId`
 * (o pedido do servidor, #276), o pedido vira atendido junto — é o atalho da
 * fila de cancelamento.
 */
export function EncerrarConsignacaoModal({
  credorId,
  consignacaoId,
  descricao,
  solicitacaoId,
  onFechar,
  onFeito,
}: {
  credorId: number;
  consignacaoId: string;
  descricao: string;
  solicitacaoId?: number;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const invalidar = useInvalidar();
  const [fim, setFim] = useState("");
  const [motivo, setMotivo] = useState("");
  const valido = competenciaOk(fim) && motivoOk(motivo);
  const salvar = useMutation({
    mutationFn: () =>
      atlas.admin.consignatarias.encerrarConsignacao(credorId, consignacaoId, {
        competenciaFim: fim, motivo: motivo.trim(), ...(solicitacaoId ? { solicitacaoId } : {}),
      }),
    onSuccess: (r) => { invalidar(); onFeito(avisoDoResultado(r)); onFechar(); },
  });
  return (
    <Modal
      open
      danger
      title={solicitacaoId ? "Encerrar e atender o pedido" : "Encerrar consignação"}
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Encerrando…" : "Encerrar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ margin: 0 }}>{descricao}.</p>
        {solicitacaoId ? <p style={{ margin: 0 }}>O pedido de cancelamento do servidor será marcado como atendido.</p> : null}
        <TextField
          label="Último desconto em"
          value={fim}
          placeholder="AAAAMM"
          hint="A competência seguinte já sai sem a linha."
          onChange={(e) => setFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <TextareaField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível encerrar.")}</p>}
      </div>
    </Modal>
  );
}

function HistoricoModal({ credorId, consignacao, onFechar }: { credorId: number; consignacao: ConsignacaoDoCredor; onFechar: () => void }) {
  const q = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "historico", consignacao.id],
    queryFn: () => atlas.admin.consignatarias.historicoConsignacao(credorId, consignacao.id),
  });
  return (
    <Modal open title={`Histórico — matrícula ${consignacao.matricula}, rubrica ${consignacao.rubrica}`} onClose={onFechar}>
      {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar o histórico.")}</p>}
      <h3 style={{ marginTop: 0 }}>Valores por competência</h3>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {(q.data?.valores ?? []).map((v) => (
          <li key={v.competenciaInicio}>
            {v.competenciaInicio}
            {v.competenciaFim === v.competenciaInicio ? "" : ` a ${v.competenciaFim ?? "sem fim"}`}:{" "}
            {v.valorCentavos === 0 ? "encerrada" : fmtBRL(v.valorCentavos / 100)} · {v.origem}
          </li>
        ))}
      </ul>
      <h3>Operações</h3>
      {(q.data?.operacoes.length ?? 0) === 0 ? (
        <p style={{ margin: 0 }}>Nenhuma operação manual: os valores vieram de arquivo, carga ou retorno.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {q.data!.operacoes.map((o) => (
            <li key={o.id}>
              {ROTULO_OPERACAO[o.tipo]} em {o.competencia} — {o.valorAntesCentavos == null ? "" : `${fmtBRL(o.valorAntesCentavos / 100)} → `}
              {o.valorDepoisCentavos === 0 ? "encerrada" : fmtBRL(o.valorDepoisCentavos / 100)}, por {o.registradaPor}
              {o.solicitacaoId ? ` (pedido ${o.solicitacaoId})` : ""}: {o.motivo}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
